import { createAsyncThunk, AnyAction } from '@reduxjs/toolkit';
import { batch } from 'react-redux';

import { GetInstrumentPriceBarsPayload } from 'api/market/dto';

import MarketService from 'api/market/MarketService';

import { addNotification } from '../notification/slice';
import {
  setMarketInstruments,
  setMarketInstrumentsStatus,
  setMarketCurrencies,
  setMarketCurrenciesStatus,
  setMarketPrices,
  setMarketPricesStatus,
  setSelectedInstrumentPrice,
  setSelectedInstrumentPriceStatus,
  setInstrumentPriceBars,
  setInstrumentPriceBarsStatus,
  setSelectedInstrument,
} from './slice';

import { throttle } from 'utils';
import { sortInstruments, getDefaultInstrument } from './helpers';
import { getNotification } from 'helpers';

import { 
  NotificationType,
  Notifications,
  RequestStatus,
  InstrumentPriceBarT,
} from 'types';

export const getMarketInstruments = createAsyncThunk(
  'market/getMarketInstruments',
  throttle(async (_: any, { dispatch, extra }: any) => {
    dispatch(setMarketInstrumentsStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      const { instruments } = await marketService.getInstruments();

      const sortedInstruments = sortInstruments(instruments);
      const selectedInstrument = getDefaultInstrument(sortedInstruments);

      batch(() => {
        dispatch(setMarketInstruments(sortedInstruments));
        dispatch(setSelectedInstrument(selectedInstrument.instrument_id));
        dispatch(setMarketInstrumentsStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setMarketInstrumentsStatus(RequestStatus.Failed));
      });
    }
  }, 1500)
) as unknown as () => AnyAction;

export const getMarketCurrencies = createAsyncThunk(
  'market/getMarketCurrencies',
  throttle(async (_: any, { dispatch, extra }: any) => {
    dispatch(setMarketCurrenciesStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      const data = await marketService.getCurrencies();

      batch(() => {
        dispatch(setMarketCurrencies(data.currencies));
        dispatch(setMarketCurrenciesStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setMarketCurrenciesStatus(RequestStatus.Failed));
      });
    }
  }, 1500)
) as unknown as () => AnyAction;

export const getMarketPrices = createAsyncThunk(
  'market/getMarketPrices',
  throttle(async (_: any, { dispatch, extra }: any) => {
    dispatch(setMarketPricesStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      const data = await marketService.getPrices();

      batch(() => {
        dispatch(setMarketPrices(data.instruments));
        dispatch(setMarketPricesStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setMarketPricesStatus(RequestStatus.Failed));
      });
    }
  }, 1500)
) as unknown as () => AnyAction;

export const getInstrumentPriceBars = createAsyncThunk(
  'market/getInstrumentPriceBars',
  throttle(async (params: GetInstrumentPriceBarsPayload, { dispatch, extra }: any) => {
    dispatch(setInstrumentPriceBarsStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      const { instrument_price_bars } = await marketService.getInstrumentPriceBars(params);

      batch(() => {
        dispatch(setInstrumentPriceBars(instrument_price_bars));
        dispatch(setInstrumentPriceBarsStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setInstrumentPriceBarsStatus(RequestStatus.Failed));
      });
    }
  }, 500)
) as unknown as (params: GetInstrumentPriceBarsPayload) => AnyAction;

export const subscribeInstrumentPrice = createAsyncThunk(
  'market/subscribeInstrumentPrice',
  throttle(async (instrument_id: string, { dispatch, extra }: any) => {
    try {
      dispatch(setSelectedInstrumentPriceStatus(RequestStatus.Pending));
      const marketService = extra.marketService as MarketService;

      marketService.subscribeInstrumentPrice(instrument_id, {
        next: ({ data: { instrument_price_bar } }: { data: { instrument_price_bar: InstrumentPriceBarT } }) => {
          batch(() => {
            dispatch(setSelectedInstrumentPrice(instrument_price_bar));
            dispatch(setSelectedInstrumentPriceStatus(RequestStatus.Success));
          });
        },
        error: () => {
          dispatch(setSelectedInstrumentPriceStatus(RequestStatus.Failed));
        },
        complete: () => {},
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setSelectedInstrumentPriceStatus(RequestStatus.Failed));
      });
    }
  }, 1000)
) as (instrument_id: string) => any;